import {
  floorPlanInnerStrokeAttrs,
  registerFloorPlanShapePainter,
  type FloorPlanShapePaintContext,
} from './paint';

/** v3 `stairs.js` `_stairsSizeCm.step_width` — cm → px via `scalePxPerCm`. */
const V3_STAIR_STEP_WIDTH_CM = 30;

function paintStairsSimple(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, false);
  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
    }),
  );
  const n = 7;
  for (let i = 1; i < n; i += 1) {
    const t = i / n;
    group.add(
      new K.Line({
        points: [0, t * h, w, t * h],
        stroke,
        strokeWidth: strokeWidth * 0.45,
        opacity: 0.85,
        listening: false,
      }),
    );
  }
}

function paintStairsDouble(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, false);
  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
    }),
  );
  const mid = w / 2;
  group.add(
    new K.Line({
      points: [mid, 0, mid, h],
      stroke,
      strokeWidth: strokeWidth * 0.8,
      listening: false,
    }),
  );
  const n = 10;
  for (let i = 1; i < n; i += 1) {
    const t = i / n;
    group.add(
      new K.Line({
        points: [0, t * h, w, t * h],
        stroke,
        strokeWidth: strokeWidth * 0.45,
        opacity: 0.85,
        listening: false,
      }),
    );
  }
}

function addStairWedgeOutline(
  K: any,
  group: any,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  stroke: string,
  strokeWidthPx: number,
): void {
  group.add(
    new K.Line({
      points: [x1, y1, x2, y2, x3, y3],
      closed: true,
      stroke,
      strokeWidth: strokeWidthPx,
      fillEnabled: false,
      lineJoin: 'miter',
      listening: false,
    }),
  );
}

/**
 * v3 `_drawSpiralCircleStairs` — square bbox (`w == h`): radial wedge outlines, inner guide arc, direction arrow.
 */
function paintSpiralCircleStairs(
  K: any,
  group: any,
  w: number,
  h: number,
  stepPx: number,
  stroke: string,
  strokeWidthPx: number,
): void {
  const arcrd = w / 2;
  const arcStep = Math.max(1, Math.floor((Math.PI * arcrd) / stepPx));
  const angStep = Math.PI / arcStep;
  const nSteps = Math.max(1, Math.floor((2 * Math.PI) / angStep) - 2);

  const iniX = w / 2;
  const iniY = h / 2;
  const thin = Math.max(0.5, strokeWidthPx * 0.5);

  let angle = 0;
  for (let i = 0; i < nSteps; i += 1) {
    const cur = angle;
    angle += angStep;
    const nxt = angle;
    const v2X = iniX + arcrd * Math.cos(cur);
    const v2Y = iniY + arcrd * Math.sin(cur);
    const v3X = iniX + arcrd * Math.cos(nxt);
    const v3Y = iniY + arcrd * Math.sin(nxt);
    addStairWedgeOutline(K, group, iniX, iniY, v2X, v2Y, v3X, v3Y, stroke, thin);
  }

  const lineIni = angStep / 2;
  const lineEnd = 2 * Math.PI - 2 * angStep - angStep / 2;
  const innerR = (w - w / 3) / 2;

  group.add(
    new K.Shape({
      stroke,
      strokeWidth: thin,
      listening: false,
      sceneFunc(context: any, shape: any) {
        context.beginPath();
        context.arc(iniX, iniY, innerR, lineIni, lineEnd, false);
        context.strokeShape(shape);
      },
    }),
  );

  const endX = iniX + innerR * Math.cos(lineEnd);
  const endY = iniY + innerR * Math.sin(lineEnd);

  const arrow = [
    { x: 0, y: 5 },
    { x: 5, y: -5 },
    { x: -5, y: -5 },
  ];
  for (const v of arrow) {
    const rx = v.x * Math.cos(lineEnd) - v.y * Math.sin(lineEnd);
    const ry = v.x * Math.sin(lineEnd) + v.y * Math.cos(lineEnd);
    v.x = rx;
    v.y = ry;
  }
  group.add(
    new K.Line({
      points: [endX + arrow[0].x, endY + arrow[0].y, endX + arrow[1].x, endY + arrow[1].y, endX + arrow[2].x, endY + arrow[2].y],
      closed: true,
      fill: stroke,
      stroke,
      strokeWidth: Math.max(0.5, thin * 0.75),
      listening: false,
    }),
  );
}

/**
 * v3 `_drawSpiralSquareStairs` — rectangular bbox: straight flight + vertical split + semicircular winders below.
 */
function paintSpiralSquareStairs(
  K: any,
  group: any,
  w: number,
  h: number,
  stepPx: number,
  stroke: string,
  strokeWidthPx: number,
): void {
  const arcrd = w / 2;
  const stepsCount = Math.max(1, Math.floor((h - arcrd) / stepPx));
  const steph = stepsCount * stepPx;
  const rowH = steph / stepsCount;
  const thick = Math.max(1, strokeWidthPx * 1.1);
  const thin = Math.max(0.5, strokeWidthPx * 0.5);

  // v3: `noFill()` then `rect` — outline only for the straight flight.
  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: steph,
      stroke,
      strokeWidth: strokeWidthPx,
      fillEnabled: false,
      listening: false,
    }),
  );

  for (let i = 1; i < stepsCount; i += 1) {
    group.add(
      new K.Line({
        points: [0, rowH * i, w, rowH * i],
        stroke,
        strokeWidth: thin,
        listening: false,
      }),
    );
  }

  const halfMid = w / 2;
  group.add(
    new K.Line({
      points: [halfMid, 0, halfMid, steph],
      stroke,
      strokeWidth: thick,
      listening: false,
    }),
  );

  const upIniY = rowH / 2;
  const upEndY = steph - rowH * 0.5 - 5;
  const upMidX = w - w / 4;
  group.add(
    new K.Line({
      points: [upMidX, upIniY, upMidX, upEndY],
      stroke,
      strokeWidth: thin,
      listening: false,
    }),
  );
  group.add(
    new K.Line({
      points: [upMidX - 5, upEndY, upMidX + 5, upEndY, upMidX, upEndY + 10],
      closed: true,
      stroke,
      strokeWidth: thin,
      fillEnabled: false,
      listening: false,
    }),
  );

  const dwIniY = rowH * 0.5 + 5;
  const dwEndY = steph - rowH * 0.5;
  const dwMidX = w / 4;
  group.add(
    new K.Line({
      points: [dwMidX, dwIniY, dwMidX, dwEndY],
      stroke,
      strokeWidth: thin,
      listening: false,
    }),
  );
  group.add(
    new K.Line({
      points: [dwMidX - 5, dwIniY, dwMidX + 5, dwIniY, dwMidX, dwIniY - 10],
      closed: true,
      stroke,
      strokeWidth: thin,
      fillEnabled: false,
      listening: false,
    }),
  );

  const v1X = arcrd;
  const v1Y = steph;
  const arcStep = Math.max(1, Math.floor((Math.PI * arcrd) / stepPx));
  const angStep = Math.PI / arcStep;
  let ang = 0;
  for (let i = 0; i < arcStep; i += 1) {
    const cur = ang;
    ang += angStep;
    const nxt = ang;
    const v2X = v1X + arcrd * Math.cos(cur);
    const v2Y = v1Y + arcrd * Math.sin(cur);
    const v3X = v1X + arcrd * Math.cos(nxt);
    const v3Y = v1Y + arcrd * Math.sin(nxt);
    addStairWedgeOutline(K, group, v1X, v1Y, v2X, v2Y, v3X, v3Y, stroke, thin);
  }
}

/** v3 `_drawSpiralStairs`: circle when `w == h`, else square+semicircle layout. */
function paintStairsSnail(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, scalePxPerCm } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, false);
  const stepPx = Math.max(2, V3_STAIR_STEP_WIDTH_CM * scalePxPerCm);

  const squareSnail = Math.abs(w - h) < 0.5;
  if (squareSnail) {
    // v3 `_drawSpiralCircleStairs`: no outer filled rect — wedges, inner arc, arrow only.
    paintSpiralCircleStairs(K, group, w, h, stepPx, stroke, strokeWidth);
    return;
  }

  paintSpiralSquareStairs(K, group, w, h, stepPx, stroke, strokeWidth);
}

registerFloorPlanShapePainter('stairs:SIMPLE', paintStairsSimple);
registerFloorPlanShapePainter('stairs:DOUBLE', paintStairsDouble);
registerFloorPlanShapePainter('stairs:SNAIL', paintStairsSnail);
